/**
 * Complete a just-in-time PO (BLU-1268: cost only, never received). The one
 * write and the one toast behind both places that complete one: the JIT PO
 * page (Complete, Bought as listed) and a To buy card (Bought as listed).
 *
 * Resolves to true or false; on failure it has already raised a destructive
 * toast in plain words (`jitPoErrorText`), so the caller only decides what
 * stays open.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import type { PurchaseOrder } from '@/features/purchase-orders';
import { jitPoErrorText } from '../lib/jit-po-error-text';

export type CompletableJustInTimeOrder = Pick<PurchaseOrder, 'id' | 'po_number' | 'location_id'>;

export function useCompleteJustInTime() {
  const { t } = useTranslation('orders');
  const utils = trpc.useUtils();
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const mutation = trpc.purchaseOrders.completeJustInTime.useMutation({
    // Returned so mutateAsync resolves onto fresh data (DESIGN.md section
    // 12.2): the page never flashes the old status after Complete.
    onSuccess: (_result, { orderId }) =>
      Promise.all([
        utils.purchaseOrders.getOrder.invalidate({ orderId }),
        utils.purchaseOrders.getJitOutcomes.invalidate({ orderId }),
        utils.purchaseOrders.getOrders.invalidate(),
      ]),
  });

  const complete = async (order: CompletableJustInTimeOrder, asListed: boolean): Promise<boolean> => {
    // Every PO has a location; if one ever does not, say so instead of sending
    // a call the server can only refuse.
    if (!order.location_id) {
      toast({ title: t('jit.toast.completeFailed'), variant: 'destructive' });
      return false;
    }
    setPendingOrderId(order.id);
    try {
      const result = await mutation.mutateAsync({ orderId: order.id, locationId: order.location_id, asListed });
      const po = result.poNumber ?? order.po_number;
      toast({
        title: t('jit.toast.completed'),
        description: result.alreadyComplete ? t('jit.toast.alreadyCompleted', { po }) : t('jit.toast.completedBody', { po }),
      });
      return true;
    } catch (error) {
      toast({ title: t('jit.toast.completeFailed'), description: jitPoErrorText(t, error), variant: 'destructive' });
      return false;
    } finally {
      setPendingOrderId(null);
    }
  };

  return { complete, pendingOrderId, pending: mutation.isPending };
}
